import React, { useEffect, useState } from 'react';
import { Alert, Spin, Button } from '@app/ui';
import { CloseOutlined } from '@ant-design/icons';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';
import waitFor from '@app/utils/waitFor';
import usePrevious from '@app/components/hooks/usePrevious';
import {
    isRunning,
    isNotStarted,
} from '@app/components/automated-param-modal/options';
import useCampaigns from '@app/components/hooks/campaigns/useCampaigns';
import sendNotification from '@app/actions/notifications/send';
import { Client } from '@app/types/api';
import './CampaignSnack.scss';

const CampaignSnack = ({
    client,
    sendNotification,
}: {
    client: Client;
    sendNotification: any;
}) => {
    const {
        data: campaigns,
        refetch: refetchCampaigns,
        isLoading,
    } = useCampaigns(client.id);
    const [currentCampaigns, setCurrentCampaigns] = useState([]);
    const previousCampaigns: any[] = usePrevious(currentCampaigns);
    const [isShowing, setShowing] = useState(false);

    useEffect(() => {
        if (!campaigns) return;
        const newCurrentCampaigns =
            campaigns.filter(
                c => c.jobStatus == isRunning || c.jobStatus == isNotStarted,
            ) || [];

        setCurrentCampaigns(newCurrentCampaigns);
    }, [campaigns]);

    useEffect(() => {
        if (currentCampaigns.length > 0) {
            setShowing(true);
        }
    }, [currentCampaigns]);

    const intervalRef = React.useRef(null);

    useEffect(() => {
        if (currentCampaigns.length === 0) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        } else {
            intervalRef.current = setInterval(() => {
                refetchCampaigns();
            }, 3000);
        }
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [currentCampaigns]);

    useEffect(() => {
        if (isLoading || !currentCampaigns) return;
        (async () => {
            const allCompleted =
                !currentCampaigns?.length &&
                previousCampaigns &&
                previousCampaigns.length > 0 &&
                intervalRef.current == null;
            if (allCompleted && isShowing) {
                sendNotification(`Campaigns processed successfully.`, {
                    description:
                        'All existing posts have been added to your campaigns and new ones will begin filtering in as they are analyzed.',
                });
                await waitFor(5000);
                setShowing(false);
            }
        })();
    }, [currentCampaigns, previousCampaigns, isLoading]);

    const handleClose = () => {
        setShowing(false);
    };

    if (!isShowing || currentCampaigns.length === 0) {
        return null;
    }

    return (
        <div className="campaign-snack-container">
            <Alert
                message={
                    <div className="campaign-snack-content">
                        <div className="campaign-snack-left">
                            <Spin size="small" />
                        </div>
                        <div className="campaign-snack-middle">
                            <div className="campaign-snack-title">
                                Campaign Populating...
                            </div>
                            {currentCampaigns.map((campaign, index) => {
                                const { name } = campaign;

                                return (
                                    <div
                                        key={`${name}_${index}`}
                                        className="campaign-snack-item"
                                    >
                                        <span className="campaign-snack-name">
                                            {name} processing...
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="campaign-snack-right">
                            <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={handleClose}
                                className="campaign-snack-close"
                            />
                        </div>
                    </div>
                }
                showIcon={false}
                className="campaign-snack-alert"
            />
        </div>
    );
};

export default connect(
    ['client'],
    combineActions({
        sendNotification,
    }),
)(CampaignSnack);
